import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, Form, Input, Select, Button, Tabs, DatePicker,
  InputNumber, Tooltip, message, Row, Col, Space, Divider,
  Alert, Badge, Typography,
} from 'antd';
import {
  ArrowLeftOutlined, ArrowRightOutlined, SaveOutlined, CloseOutlined,
  PlusOutlined, UserOutlined, EnvironmentOutlined, ShopOutlined,
  PhoneOutlined, BankOutlined, TeamOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import {
  createParticipante, updateParticipante, getCatalogosParticipante,
} from '../../services/participant/participantesServices';
import ModalZonaInfluencia from '../ZonaInfluencia/ModalZonaInfluencia';
import { getZonasInfluencia } from '../../services/participant/zonaInfluenciaService';
import {
  getPoblacionesParticipante, guardarPoblacionesParticipante,
} from '../../services/participant/poblacionParticipanteService';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

// ─── Constantes ──────────────────────────────────────────────────────────────

const TABS = [
  { key: 'personal', label: 'Personal', icon: <UserOutlined /> },
  { key: 'socioeconomico', label: 'Socioeconómico', icon: <TeamOutlined /> },
  { key: 'conyuge', label: 'Cónyuge', icon: <TeamOutlined /> },
  { key: 'ubicacion', label: 'Ubicación', icon: <EnvironmentOutlined /> },
  { key: 'productivo', label: 'Productivo', icon: <ShopOutlined /> },
  { key: 'bancario', label: 'Financiero', icon: <BankOutlined /> },
  { key: 'contacto', label: 'Contacto', icon: <PhoneOutlined /> },
];

const TAB_FIELDS = {
  personal: ['nombre_1', 'apellido_1', 'oid_tipo_identificacion', 'numero_identificacion', 'sexo', 'oid_proyecto', 'oid_implementador'],
  socioeconomico: ['ingresos_mensuales', 'oid_linea_economica', 'oid_nivel_escolaridad', 'cabeza_familia'],
  conyuge: [],
  ubicacion: ['oid_departamento', 'oid_municipio', 'oid_zona_influencia'],
  productivo: [],
  bancario: [],
  contacto: ['estado'],
};

const SI_NO_OPTIONS = [
  { value: 'SI', label: 'Sí' },
  { value: 'NO', label: 'No' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const filterOption = (input, option) =>
  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ||
  option?.label?.toString().toLowerCase().includes(input.toLowerCase());

const SiNoSelect = ({ disabled, placeholder = 'Seleccionar', ...rest }) => (
  <Select disabled={disabled} placeholder={placeholder} allowClear {...rest}>
    {SI_NO_OPTIONS.map(({ value, label }) => (
      <Option key={value} value={value}>{label}</Option>
    ))}
  </Select>
);

// Tooltip estándar para campos con ayuda
const HelpLabel = ({ label, help }) => (
  <Space size={4}>
    {label}
    <Tooltip title={help}>
      <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
    </Tooltip>
  </Space>
);

// ─── Componente principal ────────────────────────────────────────────────────

const ParticipantForm = ({ isOpen, onClose, participant, onSuccess, mode }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // Evita que el reset de "create" se repita cuando zonasInfluencia se carga después
  const createResetDone = useRef(false);
  const [catalogos, setCatalogos] = useState({});
  const [activeTab, setActiveTab] = useState('personal');
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState([]);
  const [zonasFiltradas, setZonasFiltradas] = useState([]);
  const [poblacionesSeleccionadas, setPoblacionesSeleccionadas] = useState([]);
  const [openZonaModal, setOpenZonaModal] = useState(false);
  const [zonasInfluencia, setZonasInfluencia] = useState([]);
  const [tabErrors, setTabErrors] = useState({});
  // Controla si muestra la sección de datos de cónyuge (depende del estado civil)
  const [tieneConyuge, setTieneConyuge] = useState(false);
  // Controla si el participante vive en el mismo lugar (para mostrar/ocultar campo adicional)
  const [viveMismoLugar, setViveMismoLugar] = useState(null);
  // Controla si está vinculado a organización
  const [vinculadoOrg, setVinculadoOrg] = useState(false);

  const isReadOnly = mode === 'view';

  // ─── Carga de datos ──────────────────────────────────────────────────────

  const fetchZonasInfluencia = useCallback(async () => {
    const response = await getZonasInfluencia();
    if (response.status === 200) setZonasInfluencia(response.data);
  }, []);

  useEffect(() => { fetchZonasInfluencia(); }, [fetchZonasInfluencia]);

  useEffect(() => {
    if (isOpen) loadCatalogos();
  }, [isOpen]);

  useEffect(() => {
    const loadParticipantData = async () => {
      if (!isOpen) return;

      if ((mode === 'edit' || mode === 'view') && participant && catalogos.municipios && zonasInfluencia.length > 0) {
        const values = { ...participant };

        // Fecha
        if (values.fecha_nacimiento) values.fecha_nacimiento = dayjs(values.fecha_nacimiento);

        // Convierte IDs a string para los Select
        const idFields = [
          'oid_tipo_identificacion', 'oid_linea_economica', 'oid_nivel_escolaridad',
          'oid_estado_civil', 'oid_departamento', 'oid_municipio', 'oid_zona_influencia',
          'tenencia_predio', 'oid_medio_transporte', 'oid_linea_productiva',
          'oid_variedad_establecida', 'oid_linea_productiva_segundaria',
          'oid_tipo_identificacion_conyuge', 'oid_implementador', 'oid_proyecto', 'estado',
          'oid_organizacion',
          // bancaria
          'oid_tipo_establecimiento_bancario_cercano', 'oid_tipo_entidad',
        ];
        idFields.forEach((f) => {
          if (values[f] != null) values[f] = values[f].toString();
        });

        // Aplana informacion_bancaria al form.
        // Puede venir como objeto anidado (desde getOne) o plano (desde la lista).
        if (participant.informacion_bancaria) {
          // Ruta getOne: viene anidado
          const ib = participant.informacion_bancaria;
          const bancariaOidFields = ['oid_tipo_establecimiento_bancario_cercano', 'oid_tipo_entidad'];
          Object.keys(ib).forEach((k) => {
            if (k !== 'oid' && k !== 'oid_participante') {
              const val = ib[k];
              values[k] = val != null ? val.toString() : val;
            }
          });
        } else {
          // Ruta lista: campos bancarios ya vienen planos en el objeto participante
          // Solo necesitan conversión a string (ya incluidos en idFields arriba para los OIDs)
          const bancariaStringFields = [
            'cuenta_con_productos_financieros', 'cuenta_de_ahorro', 'tarjeta_de_credito',
            'otros_creditos', 'recibio_capacitacion_educacion_financiera',
            'interesado_informacion_inclusion_financiera',
          ];
          bancariaStringFields.forEach((k) => {
            if (values[k] == null) values[k] = undefined; // no sobreescribe con null
          });
        }

        // Organización
        if (participant.oid_organizacion) {
          values.oid_organizacion = participant.oid_organizacion.toString();
        }

        // Municipio → filtra municipios y zonas
        if (values.oid_municipio) {
          const mun = catalogos.municipios.find(
            (m) => m.oid.toString() === values.oid_municipio
          );
          if (mun) {
            const deptoId = mun.oid_depto?.toString();
            setMunicipiosFiltrados(
              catalogos.municipios.filter((m) => m.oid_depto?.toString() === deptoId)
            );
            setZonasFiltradas(
              zonasInfluencia.filter(
                (z) => z.id_municipio?.toString() === values.oid_municipio?.toString()
              )
            );
          }
        }

        // Estado civil → ¿tiene cónyuge?
        detectaTieneConyuge(values.oid_estado_civil, catalogos.estados_civiles);

        // Vive mismo lugar
        setViveMismoLugar(values.vive_mismo_lugar);

        // Vinculado a organización
        setVinculadoOrg(values.vinculado_alguna_organizacion === 'SI');

        setTimeout(() => form.setFieldsValue(values), 0);

        // Poblaciones
        if (participant.oid) {
          const pobResponse = await getPoblacionesParticipante(participant.oid);
          if (pobResponse.status === 200) setPoblacionesSeleccionadas(pobResponse.data);
        }
      } else if (mode === 'create' && isOpen && !createResetDone.current) {
        createResetDone.current = true;
        form.resetFields();
        setActiveTab('personal');
        setPoblacionesSeleccionadas([]);
        setTieneConyuge(false);
        setViveMismoLugar(null);
        setVinculadoOrg(false);
        setMunicipiosFiltrados([]);
        setZonasFiltradas([]);
        setTabErrors({});
      }
    };

    loadParticipantData();
  }, [isOpen, participant, mode, catalogos, zonasInfluencia]);

  const loadCatalogos = async () => {
    try {
      const { status, data } = await getCatalogosParticipante();
      if (status === 200) {
        setCatalogos(data);
        setMunicipiosFiltrados([]);
      }
    } catch (error) {
      console.error('Error loading catalogs:', error);
      message.error('Error al cargar los catálogos');
    }
  };

  // ─── Lógica de estado civil → cónyuge ───────────────────────────────────

  const detectaTieneConyuge = (oidEstadoCivil, estados) => {
    if (!oidEstadoCivil || !estados) { setTieneConyuge(false); return; }
    const estado = estados.find((e) => e.oid.toString() === oidEstadoCivil?.toString());
    if (!estado) { setTieneConyuge(false); return; }
    const texto = estado.estado_civil?.toLowerCase() || '';
    const conConyuge = texto.includes('casad') || texto.includes('union') || texto.includes('unión') || texto.includes('compañer');
    setTieneConyuge(conConyuge);
  };

  // ─── Handlers de cascada ─────────────────────────────────────────────────

  const handleDepartamentoChange = (oidDepartamento) => {
    form.setFieldsValue({ oid_municipio: undefined, oid_zona_influencia: undefined });
    setZonasFiltradas([]);
    if (!oidDepartamento) { setMunicipiosFiltrados([]); return; }
    setMunicipiosFiltrados(
      catalogos.municipios?.filter((m) => m.oid_depto?.toString() === oidDepartamento?.toString()) || []
    );
  };

  const handleMunicipioChange = (oidMunicipio) => {
    form.setFieldsValue({ oid_zona_influencia: undefined });
    if (!oidMunicipio) { setZonasFiltradas([]); return; }
    setZonasFiltradas(
      zonasInfluencia.filter((z) => z.id_municipio?.toString() === oidMunicipio?.toString()) || []
    );
  };

  const handleEstadoCivilChange = (value) => {
    detectaTieneConyuge(value, catalogos.estados_civiles);
  };

  // ─── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (isReadOnly) return;
    try {
      setLoading(true);
      let values;
      try {
        const allFields = form.getFieldsValue(true);
        values = await form.validateFields();
        values = { ...allFields, ...values };
      } catch (error) {
        if (error.errorFields?.length > 0) {
          message.error('Completa los campos obligatorios antes de guardar');
          const firstField = error.errorFields[0].name[0];
          const targetTab = Object.keys(TAB_FIELDS).find((tab) =>
            TAB_FIELDS[tab].includes(firstField)
          );
          if (targetTab) {
            setActiveTab(targetTab);
            setTimeout(() => form.scrollToField(error.errorFields[0].name), 100);
          }
          return;
        }
        throw error;
      }

      const transformedValues = transformParticipanteData(values);
      // Agrega poblaciones como array
      transformedValues._poblaciones = poblacionesSeleccionadas;

      let response;
      if (mode === 'create') {
        response = await createParticipante(transformedValues);
      } else {
        response = await updateParticipante(
          participant.oid,
          transformedValues,
          participant.oid_participante_proyecto
        );
      }

      if (response?.status === 200 || response?.status === 201) {
        const oid_participante =
          mode === 'create' ? response.data.oid_participante : participant.oid;
        await guardarPoblacionesParticipante(oid_participante, poblacionesSeleccionadas);

        message.success(
          mode === 'create' ? 'Participante creado correctamente' : 'Participante actualizado correctamente'
        );
        createResetDone.current = false;
        onClose();
        onSuccess?.();
      } else if (response?.status === 409) {
        message.warning(response.message || 'Ya existe un participante con ese número de identificación');
      } else if (response) {
        throw new Error(response.message || 'Error en la operación');
      }
    } catch (error) {
      console.error('Error:', error);
      if (!error.errorFields) message.error(error.message || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const transformParticipanteData = (data) => {
    const allowed = [
      // Identificación
      'oid', 'nombre_participante', 'nombre_1', 'nombre_2', 'apellido_1', 'apellido_2',
      'oid_tipo_identificacion', 'numero_identificacion', 'sexo', 'fecha_nacimiento',
      // Socioeconómico
      'sabe_leer_escribir', 'ingresos_mensuales', 'oid_linea_economica', 'oid_nivel_escolaridad',
      'cabeza_familia', 'num_personas_cargo', 'total_nucleo_familiar', 'oid_estado_civil',
      'vinculado_alguna_organizacion', 'oid_organizacion',
      // Cónyuge
      'nombre_conyuge', 'oid_tipo_identificacion_conyuge', 'identificacion_conyuge', 'numero_contacto_conyuge',
      // Ubicación
      'oid_municipio', 'oid_zona_influencia', 'codigo_shape', 'nombre_predio', 'tenencia_predio',
      'latitud', 'longitud', 'distancia_cabecera_municipal', 'tiempo_cabecera_municipal',
      'oid_medio_transporte', 'vive_mismo_lugar', 'lugar_vivienda',
      // Productivo
      'num_hectarias_proyecto', 'oid_linea_productiva', 'oid_variedad_establecida',
      'oid_linea_productiva_segundaria', 'num_hectareas', 'produccion_anual',
      // Bancario
      'oid_tipo_establecimiento_bancario_cercano', 'cuenta_con_productos_financieros',
      'cuenta_de_ahorro', 'tarjeta_de_credito', 'otros_creditos', 'oid_tipo_entidad',
      'recibio_capacitacion_educacion_financiera', 'interesado_informacion_inclusion_financiera',
      // Contacto
      'direccion_correspondencia', 'numero_telefonico_1', 'numero_telefonico_2',
      'correo_electronico', 'telefonica', 'internet', 'estado',
      // Proyecto
      'oid_proyecto', 'oid_implementador',
    ];

    const result = {};
    allowed.forEach((f) => { if (data[f] !== undefined) result[f] = data[f]; });

    // Construye nombre_participante desde campos separados
    if (result.nombre_1 || result.apellido_1) {
      result.nombre_participante = [result.nombre_1, result.nombre_2, result.apellido_1, result.apellido_2]
        .filter(Boolean).join(' ').trim();
    }

    // Fecha
    if (result.fecha_nacimiento && dayjs.isDayjs(result.fecha_nacimiento)) {
      result.fecha_nacimiento = result.fecha_nacimiento.format('YYYY-MM-DD');
    }

    // Numéricos
    ['ingresos_mensuales', 'num_personas_cargo', 'total_nucleo_familiar',
      'distancia_cabecera_municipal', 'tiempo_cabecera_municipal',
      'num_hectarias_proyecto', 'num_hectareas'].forEach((f) => {
        if (result[f] != null) result[f] = Number(result[f]);
      });

    // Enteros / FK
    ['oid_tipo_identificacion', 'oid_linea_economica', 'oid_nivel_escolaridad', 'oid_estado_civil',
      'oid_municipio', 'oid_zona_influencia', 'tenencia_predio', 'oid_medio_transporte',
      'oid_linea_productiva', 'oid_variedad_establecida', 'oid_linea_productiva_segundaria',
      'oid_tipo_identificacion_conyuge', 'oid_implementador', 'oid_proyecto', 'estado',
      'oid_organizacion', 'oid_tipo_establecimiento_bancario_cercano', 'oid_tipo_entidad',
    ].forEach((f) => {
      if (result[f] != null) {
        const p = parseInt(String(result[f]), 10);
        result[f] = isNaN(p) ? null : p;
      }
    });

    // Nullifica vacíos
    Object.keys(result).forEach((k) => {
      if (result[k] === undefined) result[k] = null;
    });

    return result;
  };

  // ─── Validación por pestaña ──────────────────────────────────────────────

  const validateCurrentTab = async () => {
    try {
      await form.validateFields(TAB_FIELDS[activeTab]);
      setTabErrors((prev) => ({ ...prev, [activeTab]: false }));
      return true;
    } catch (error) {
      if (error.errorFields?.length > 0) {
        message.warning(error.errorFields[0].errors[0]);
        form.scrollToField(error.errorFields[0].name);
        setTabErrors((prev) => ({ ...prev, [activeTab]: true }));
      }
      return false;
    }
  };

  const navigateTabs = async (direction) => {
    const isValid = await validateCurrentTab();
    if (!isValid) return;
    const idx = TABS.findIndex((t) => t.key === activeTab);
    if (direction === 'prev' && idx > 0) setActiveTab(TABS[idx - 1].key);
    if (direction === 'next' && idx < TABS.length - 1) setActiveTab(TABS[idx + 1].key);
  };

  const handleCancel = () => {
    createResetDone.current = false;
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: 'Hay cambios sin guardar',
        content: '¿Seguro que quieres salir? Se perderán los cambios.',
        okText: 'Sí, salir',
        cancelText: 'No, volver',
        onOk: () => { form.resetFields(); onClose(); },
      });
    } else {
      onClose();
    }
  };

  // ─── Secciones del formulario ────────────────────────────────────────────

  const renderPersonalInfo = () => (
    <>
      <Alert
        type="info"
        showIcon
        message="Ingresa el nombre tal como aparece en el documento de identidad."
        style={{ marginBottom: 20 }}
      />
      <Row gutter={[16, 0]}>
        {/* Nombres */}
        <Col xs={24} sm={12} lg={6}>
          <Form.Item
            name="nombre_1"
            label="Primer Nombre"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input disabled={isReadOnly} placeholder="Ej: María" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Form.Item name="nombre_2" label="Segundo Nombre">
            <Input disabled={isReadOnly} placeholder="Ej: Fernanda (opcional)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Form.Item
            name="apellido_1"
            label="Primer Apellido"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input disabled={isReadOnly} placeholder="Ej: García" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Form.Item name="apellido_2" label="Segundo Apellido">
            <Input disabled={isReadOnly} placeholder="Ej: López (opcional)" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="oid_tipo_identificacion"
            label="Tipo de Identificación"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select disabled={isReadOnly} placeholder="Seleccionar tipo" showSearch filterOption={filterOption}>
              {catalogos.tipos_identificacion?.map((t) => (
                <Option key={t.oid} value={t.oid.toString()}>{t.tipo_identificacion}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="numero_identificacion"
            label="Número de Identificación"
            rules={[
              { required: true, message: 'Requerido' },
              { pattern: /^[0-9A-Za-z-]+$/, message: 'Solo números y letras' },
            ]}
          >
            <Input disabled={isReadOnly} placeholder="Ej: 1012345678" maxLength={20} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="sexo"
            label="Sexo"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select disabled={isReadOnly} placeholder="Seleccionar">
              <Option value="Hombre">Hombre</Option>
              <Option value="Mujer">Mujer</Option>
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="fecha_nacimiento"
            label="Fecha de Nacimiento"
          >
            <DatePicker
              disabled={isReadOnly}
              placeholder="DD/MM/AAAA"
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              disabledDate={(current) => current && current > dayjs().endOf('day')}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item name="sabe_leer_escribir" label="¿Sabe Leer y Escribir?">
            <SiNoSelect disabled={isReadOnly} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Divider orientation="left" plain>
            <Text type="secondary" style={{ fontSize: 12 }}>Proyecto asignado</Text>
          </Divider>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="oid_proyecto"
            label="Proyecto"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select
              showSearch allowClear disabled={isReadOnly}
              placeholder="Seleccionar proyecto"
              filterOption={filterOption}
            >
              {catalogos.proyectos?.map((p) => (
                <Option key={p.oid} value={p.oid.toString()}>
                  {p.codigo} - {p.proyecto}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="oid_implementador"
            label="Implementador"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select disabled={isReadOnly} placeholder="Seleccionar implementador" showSearch filterOption={filterOption}>
              {catalogos.implementadores?.map((i) => (
                <Option key={i.oid} value={i.oid.toString()}>{i.Implemetador}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const renderSocioeconomicInfo = () => (
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item
          name="ingresos_mensuales"
          label={<HelpLabel label="Ingresos Mensuales" help="Ingresos promedio del hogar en pesos colombianos" />}
        >
          <InputNumber
            disabled={isReadOnly}
            style={{ width: '100%' }}
            min={0}
            formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
            placeholder="0"
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="oid_linea_economica" label="Línea Económica Principal">
          <Select showSearch allowClear disabled={isReadOnly} placeholder="Seleccionar" filterOption={filterOption}>
            {catalogos.lineas_economicas?.map((l) => (
              <Option key={l.oid} value={l.oid.toString()}>{l.linea_economica_principal}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="oid_nivel_escolaridad" label="Nivel de Escolaridad">
          <Select disabled={isReadOnly} placeholder="Seleccionar" showSearch filterOption={filterOption}>
            {catalogos.niveles_escolaridad?.map((n) => (
              <Option key={n.oid} value={n.oid.toString()}>{n.nivel_escolaridad}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="oid_estado_civil" label="Estado Civil">
          <Select
            disabled={isReadOnly} placeholder="Seleccionar" showSearch filterOption={filterOption}
            onChange={handleEstadoCivilChange}
          >
            {catalogos.estados_civiles?.map((e) => (
              <Option key={e.oid} value={e.oid.toString()}>{e.estado_civil}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="cabeza_familia" label="¿Es Cabeza de Familia?">
          <SiNoSelect disabled={isReadOnly} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item
          name="num_personas_cargo"
          label={<HelpLabel label="Personas a Cargo" help="Número de personas que dependen económicamente del participante" />}
        >
          <InputNumber disabled={isReadOnly} min={0} max={20} style={{ width: '100%' }} placeholder="0" />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item
          name="total_nucleo_familiar"
          label={<HelpLabel label="Total Núcleo Familiar" help="Total de personas que conviven en el hogar incluyendo al participante" />}
        >
          <InputNumber disabled={isReadOnly} min={0} max={30} style={{ width: '100%' }} placeholder="0" />
        </Form.Item>
      </Col>

      <Col xs={24}>
        <Divider orientation="left" plain>
          <Text type="secondary" style={{ fontSize: 12 }}>Organización</Text>
        </Divider>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="vinculado_alguna_organizacion" label="¿Vinculado a Alguna Organización?">
          <Select
            disabled={isReadOnly} placeholder="Seleccionar"
            onChange={(v) => {
              setVinculadoOrg(v === 'SI');
              if (v !== 'SI') form.setFieldsValue({ oid_organizacion: undefined });
            }}
          >
            <Option value="SI">Sí</Option>
            <Option value="NO">No</Option>
          </Select>
        </Form.Item>
      </Col>
      {vinculadoOrg && (
        <Col xs={24} sm={16} lg={16}>
          <Form.Item
            name="oid_organizacion"
            label="Organización"
            tooltip="Selecciona la organización a la que pertenece el participante"
          >
            <Select showSearch allowClear disabled={isReadOnly} placeholder="Buscar organización" filterOption={filterOption}>
              {catalogos.organizaciones?.map((o) => (
                <Option key={o.oid} value={o.oid.toString()}>
                  {o.sigla ? `${o.sigla} — ` : ''}{o.organizacion}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      )}

      <Col xs={24}>
        <Divider orientation="left" plain>
          <Text type="secondary" style={{ fontSize: 12 }}>Poblaciones (puede seleccionar varias)</Text>
        </Divider>
      </Col>
      <Col xs={24}>
        <Form.Item label="Población / Grupo Étnico">
          <Select
            mode="multiple" allowClear disabled={isReadOnly}
            placeholder="Seleccione la(s) población(es) a la que pertenece"
            value={poblacionesSeleccionadas}
            onChange={setPoblacionesSeleccionadas}
            options={catalogos.poblaciones?.map((p) => ({ value: p.oid, label: p.poblacion }))}
            filterOption={filterOption}
            maxTagCount="responsive"
          />
        </Form.Item>
      </Col>
    </Row>
  );

  const renderSpouseInfo = () => (
    <>
      {!tieneConyuge && (
        <Alert
          type="warning"
          showIcon
          message="Esta sección aplica para participantes casados o en unión libre. Selecciona el estado civil correspondiente en la pestaña Socioeconómico para habilitar los campos."
          style={{ marginBottom: 20 }}
        />
      )}
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item name="nombre_conyuge" label="Nombre del Cónyuge / Compañero(a)">
            <Input disabled={isReadOnly || !tieneConyuge} placeholder="Nombre completo" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Form.Item name="oid_tipo_identificacion_conyuge" label="Tipo de Documento Cónyuge">
            <Select disabled={isReadOnly || !tieneConyuge} placeholder="Tipo" showSearch filterOption={filterOption} allowClear>
              {catalogos.tipos_identificacion?.map((t) => (
                <Option key={t.oid} value={t.oid.toString()}>{t.tipo_identificacion}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Form.Item name="identificacion_conyuge" label="Número de Documento Cónyuge">
            <Input disabled={isReadOnly || !tieneConyuge} placeholder="Ej: 1056789012" maxLength={12} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="numero_contacto_conyuge" label="Teléfono del Cónyuge">
            <Input disabled={isReadOnly || !tieneConyuge} placeholder="Ej: 3001234567" maxLength={25} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const renderLocationInfo = () => (
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="oid_departamento" label="Departamento">
          <Select
            showSearch allowClear disabled={isReadOnly} placeholder="Seleccionar departamento"
            onChange={handleDepartamentoChange} filterOption={filterOption}
          >
            {catalogos.departamentos?.map((d) => (
              <Option key={d.oid} value={d.oid.toString()}>{d.departamento}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item
          name="oid_municipio" label="Municipio"
          rules={[{ required: true, message: 'Requerido' }]}
        >
          <Select
            showSearch allowClear disabled={isReadOnly} placeholder="Primero selecciona un departamento"
            onChange={handleMunicipioChange} filterOption={filterOption}
          >
            {municipiosFiltrados.map((m) => (
              <Option key={m.oid} value={m.oid.toString()}>{m.municipio}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <Form.Item
            name="oid_zona_influencia" label="Zona de Influencia"
            rules={[{ required: true, message: 'Requerido' }]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <Select
              showSearch allowClear
              disabled={isReadOnly || !form.getFieldValue('oid_municipio')}
              placeholder={form.getFieldValue('oid_municipio') ? 'Seleccionar zona' : 'Selecciona un municipio primero'}
              options={zonasFiltradas.map((z) => ({ value: z.oid?.toString(), label: z.zona_influencia }))}
              filterOption={filterOption}
            />
          </Form.Item>
          {!isReadOnly && (
            <Form.Item label=" " style={{ marginBottom: 0 }}>
              <Tooltip title="Crear nueva zona de influencia">
                <Button
                  type="dashed" icon={<PlusOutlined />}
                  onClick={() => setOpenZonaModal(true)}
                  disabled={!form.getFieldValue('oid_municipio')}
                />
              </Tooltip>
            </Form.Item>
          )}
        </div>
      </Col>

      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="codigo_shape" label={<HelpLabel label="Código Shape" help="Código del polígono georreferenciado asociado al predio" />}>
          <Input disabled={isReadOnly} placeholder="Ej: 19532032" maxLength={10} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="nombre_predio" label="Nombre del Predio">
          <Input disabled={isReadOnly} placeholder="Ej: La Esperanza" maxLength={50} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="tenencia_predio" label="Tenencia del Predio">
          <Select disabled={isReadOnly} placeholder="Seleccionar" showSearch filterOption={filterOption} allowClear>
            {catalogos.tenencia_predios?.map((t) => (
              <Option key={t.oid} value={t.oid.toString()}>{t.relacion_tenencia_predio}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      <Col xs={24} sm={12} lg={6}>
        <Form.Item
          name="latitud"
          label={<HelpLabel label="Latitud" help="Coordenada geográfica del predio. Formato decimal, ej: 2.4560" />}
        >
          <Input disabled={isReadOnly} placeholder="Ej: 2.4560" />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Form.Item
          name="longitud"
          label={<HelpLabel label="Longitud" help="Coordenada geográfica del predio. Formato decimal, ej: -76.6120" />}
        >
          <Input disabled={isReadOnly} placeholder="Ej: -76.6120" />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Form.Item
          name="distancia_cabecera_municipal"
          label={<HelpLabel label="Distancia Cabecera (Km)" help="Kilómetros desde el predio hasta la cabecera municipal" />}
        >
          <InputNumber disabled={isReadOnly} min={0} step={0.5} style={{ width: '100%' }} placeholder="0" />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Form.Item
          name="tiempo_cabecera_municipal"
          label={<HelpLabel label="Tiempo Cabecera (Min)" help="Minutos de viaje desde el predio hasta la cabecera municipal" />}
        >
          <InputNumber disabled={isReadOnly} min={0} style={{ width: '100%' }} placeholder="0" />
        </Form.Item>
      </Col>

      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="oid_medio_transporte" label="Medio de Transporte Principal">
          <Select disabled={isReadOnly} placeholder="Seleccionar" showSearch filterOption={filterOption} allowClear>
            {catalogos.medios_transporte?.map((m) => (
              <Option key={m.oid} value={m.oid.toString()}>{m.medio_trasporte}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item
          name="vive_mismo_lugar"
          label={<HelpLabel label="¿Vive en el Mismo Lugar del Proyecto?" help="Indica si el participante reside en el mismo predio donde se ejecuta el proyecto" />}
        >
          <Select
            disabled={isReadOnly} placeholder="Seleccionar" allowClear
            onChange={(v) => setViveMismoLugar(v)}
          >
            <Option value="SI">Sí</Option>
            <Option value="NO">No</Option>
          </Select>
        </Form.Item>
      </Col>

      {viveMismoLugar === 'NO' && (
        <Col xs={24} sm={24} lg={8}>
          <Form.Item name="lugar_vivienda" label="Lugar de Vivienda (si es diferente al predio)">
            <Input disabled={isReadOnly} placeholder="Vereda / Barrio / Dirección" maxLength={100} />
          </Form.Item>
        </Col>
      )}
    </Row>
  );

  const renderProductiveInfo = () => (
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="oid_linea_productiva" label="Línea Productiva Principal">
          <Select showSearch allowClear disabled={isReadOnly} placeholder="Seleccionar" filterOption={filterOption}>
            {catalogos.lineas_productivas?.map((l) => (
              <Option key={l.oid} value={l.oid.toString()}>{l.linea_productiva}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item
          name="oid_variedad_establecida"
          label={<HelpLabel label="Variedad Establecida" help="Variedad del cultivo o especie principal trabajada" />}
        >
          <Select showSearch allowClear disabled={isReadOnly} placeholder="Seleccionar variedad" filterOption={filterOption}>
            {catalogos.variedades_establecidas?.map((v) => (
              <Option key={v.oid} value={v.oid.toString()}>{v.variedad_establecida}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="oid_linea_productiva_segundaria" label="Línea Productiva Secundaria">
          <Select showSearch allowClear disabled={isReadOnly} placeholder="Seleccionar" filterOption={filterOption}>
            {catalogos.lineas_productivas?.map((l) => (
              <Option key={l.oid} value={l.oid.toString()}>{l.linea_productiva}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item
          name="num_hectarias_proyecto"
          label={<HelpLabel label="Hectáreas del Proyecto" help="Extensión de tierra vinculada al proyecto" />}
        >
          <InputNumber disabled={isReadOnly} min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" addonAfter="Ha" />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item
          name="num_hectareas"
          label={<HelpLabel label="Hectáreas Totales del Predio" help="Área total del predio del participante" />}
        >
          <InputNumber disabled={isReadOnly} min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" addonAfter="Ha" />
        </Form.Item>
      </Col>
      <Col xs={24}>
        <Form.Item
          name="produccion_anual"
          label={<HelpLabel label="Producción Anual" help="Descripción de la producción estimada por año" />}
        >
          <TextArea disabled={isReadOnly} rows={3} placeholder="Ej: 2 toneladas de café pergamino por año..." maxLength={200} showCount />
        </Form.Item>
      </Col>
    </Row>
  );

  const renderBancariaInfo = () => (
    <>
      <Alert
        type="info" showIcon
        message="Esta sección recoge información sobre el acceso del participante al sistema financiero."
        style={{ marginBottom: 20 }}
      />
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="oid_tipo_establecimiento_bancario_cercano"
            label={<HelpLabel label="Establecimiento Bancario Más Cercano" help="Tipo de punto de acceso financiero más próximo al predio" />}
          >
            <Select showSearch allowClear disabled={isReadOnly} placeholder="Seleccionar" filterOption={filterOption}>
              {catalogos.establecimientos_bancarios?.map((e) => (
                <Option key={e.oid} value={e.oid.toString()}>{e.establemcimiento_bancario}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item name="cuenta_con_productos_financieros" label="¿Tiene Productos Financieros?">
            <SiNoSelect disabled={isReadOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item name="oid_tipo_entidad" label="Tipo de Entidad Financiera">
            <Select showSearch allowClear disabled={isReadOnly} placeholder="Seleccionar" filterOption={filterOption}>
              {catalogos.tipos_entidad_bank?.map((e) => (
                <Option key={e.oid} value={e.oid.toString()}>{e.tipo_entidad}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Divider orientation="left" plain>
            <Text type="secondary" style={{ fontSize: 12 }}>Productos que posee</Text>
          </Divider>
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Form.Item name="cuenta_de_ahorro" label="¿Cuenta de Ahorro?">
            <SiNoSelect disabled={isReadOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Form.Item name="tarjeta_de_credito" label="¿Tarjeta de Crédito?">
            <SiNoSelect disabled={isReadOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Form.Item name="otros_creditos" label="¿Otros Créditos?">
            <SiNoSelect disabled={isReadOnly} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Divider orientation="left" plain>
            <Text type="secondary" style={{ fontSize: 12 }}>Educación financiera</Text>
          </Divider>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="recibio_capacitacion_educacion_financiera" label="¿Recibió Capacitación en Educación Financiera?">
            <SiNoSelect disabled={isReadOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="interesado_informacion_inclusion_financiera" label="¿Interesado en Información sobre Inclusión Financiera?">
            <SiNoSelect disabled={isReadOnly} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const renderContactInfo = () => (
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="numero_telefonico_1" label="Teléfono Principal">
          <Input
            disabled={isReadOnly} placeholder="Ej: 3001234567" maxLength={20}
            addonBefore={<PhoneOutlined />}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item name="numero_telefonico_2" label="Teléfono Alternativo">
          <Input disabled={isReadOnly} placeholder="Ej: 6081234567" maxLength={20} addonBefore={<PhoneOutlined />} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Form.Item
          name="correo_electronico"
          label="Correo Electrónico"
          rules={[{ type: 'email', message: 'Formato de correo inválido' }]}
        >
          <Input disabled={isReadOnly} placeholder="Ej: usuario@correo.com" maxLength={50} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Form.Item
          name="telefonica"
          label={<HelpLabel label="¿Tiene Servicio Telefónico?" help="Cobertura de señal o servicio telefónico en el lugar de vivienda" />}
        >
          <SiNoSelect disabled={isReadOnly} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Form.Item
          name="internet"
          label={<HelpLabel label="¿Tiene Acceso a Internet?" help="Acceso a internet en el hogar o en las cercanías del predio" />}
        >
          <SiNoSelect disabled={isReadOnly} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Form.Item
          name="estado"
          label="Estado del Registro"
          initialValue="1"
          rules={[{ required: true, message: 'Requerido' }]}
        >
          <Select disabled={isReadOnly}>
            <Option value="1">Activo</Option>
            <Option value="2">Inactivo</Option>
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24}>
        <Form.Item name="direccion_correspondencia" label="Dirección de Correspondencia">
          <TextArea
            disabled={isReadOnly} rows={3}
            placeholder="Vereda, municipio, departamento o dirección urbana donde recibe correo..."
            maxLength={100} showCount
          />
        </Form.Item>
      </Col>
    </Row>
  );

  // ─── Tab items con badge de error ────────────────────────────────────────

  const tabItems = [
    { key: 'personal', children: renderPersonalInfo() },
    { key: 'socioeconomico', children: renderSocioeconomicInfo() },
    { key: 'conyuge', children: renderSpouseInfo() },
    { key: 'ubicacion', children: renderLocationInfo() },
    { key: 'productivo', children: renderProductiveInfo() },
    { key: 'bancario', children: renderBancariaInfo() },
    { key: 'contacto', children: renderContactInfo() },
  ].map((item) => {
    const meta = TABS.find((t) => t.key === item.key);
    return {
      ...item,
      label: (
        <Badge dot={tabErrors[item.key]} offset={[4, 0]}>
          <Space size={4}>
            {meta?.icon}
            <span>{meta?.label}</span>
          </Space>
        </Badge>
      ),
    };
  });

  // ─── Footer ───────────────────────────────────────────────────────────────

  const currentTabIndex = TABS.findIndex((t) => t.key === activeTab);

  const footerContent = (
    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
      <Space>
        {currentTabIndex > 0 && (
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigateTabs('prev')}>
            Anterior
          </Button>
        )}
        {currentTabIndex < TABS.length - 1 && (
          <Button type="primary" icon={<ArrowRightOutlined />} onClick={() => navigateTabs('next')} ghost>
            Siguiente
          </Button>
        )}
      </Space>
      <Space>
        <Button onClick={handleCancel} icon={<CloseOutlined />}>
          {isReadOnly ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!isReadOnly && (
          <Button type="primary" loading={loading} onClick={handleSubmit} icon={<SaveOutlined />}>
            {mode === 'create' ? 'Crear Participante' : 'Guardar Cambios'}
          </Button>
        )}
      </Space>
    </div>
  );

  const getModalTitle = () => {
    const titles = { create: 'Crear Participante', edit: 'Editar Participante', view: 'Ver Participante' };
    return titles[mode] || 'Participante';
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Modal
      title={
        <Space>
          <UserOutlined />
          {getModalTitle()}
          {mode === 'view' && <Badge status="processing" text="Solo lectura" />}
        </Space>
      }
      open={isOpen}
      onCancel={handleCancel}
      footer={footerContent}
      width="95%"
      style={{ maxWidth: 1200 }}
      styles={{ body: { padding: '16px 20px', maxHeight: '72vh', overflowY: 'auto' } }}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        disabled={isReadOnly}
        initialValues={{ estado: '1' }}
        scrollToFirstError
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          items={tabItems}
          size="small"
          style={{ marginBottom: 0 }}
        />
      </Form>

      <ModalZonaInfluencia
        open={openZonaModal}
        onClose={() => setOpenZonaModal(false)}
        reloadData={fetchZonasInfluencia}
        onCreated={async (newZona) => {
          const oidMunicipioActual = form.getFieldValue('oid_municipio');
          const zonaNueva = {
            oid: newZona.oid,
            zona_influencia: newZona.zona_influencia,
            id_municipio: Number(oidMunicipioActual),
            id_tipo_zona_influencia: newZona.id_tipo_zona_influencia,
          };
          const zonasActualizadas = [...zonasInfluencia, zonaNueva];
          setZonasInfluencia(zonasActualizadas);
          setZonasFiltradas(
            zonasActualizadas.filter(
              (z) => z.id_municipio?.toString() === oidMunicipioActual?.toString()
            )
          );
          form.setFieldValue('oid_zona_influencia', zonaNueva.oid.toString());
          setOpenZonaModal(false);
        }}
      />
    </Modal>
  );
};

export default ParticipantForm;